// components/common/ErrorBoundary.jsx
import React from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, Mail } from 'lucide-react';
import { Button } from '../ui';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            eventId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        this.setState({
            error,
            errorInfo,
            eventId: this.generateEventId()
        });

        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }

        // Log to external service in production
        if (process.env.NODE_ENV === 'production') {
            this.logErrorToService(error, errorInfo);
        }
    }

    generateEventId = () => {
        return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    logErrorToService = async (error, errorInfo) => {
        try {
            // Example: Send to Sentry, LogRocket, or custom logging service
            const errorData = {
                message: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: window.location.href,
                eventId: this.state.eventId
            };

            // Replace with your actual logging service
            // await fetch('/api/log-error', {
            //   method: 'POST',
            //   headers: { 'Content-Type': 'application/json' },
            //   body: JSON.stringify(errorData)
            // });

            console.log('Error logged:', errorData);
        } catch (loggingError) {
            console.error('Failed to log error:', loggingError);
        }
    };

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            eventId: null
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/dashboard';
    };

    reportIssue = () => {
        const subject = encodeURIComponent(`Error Report - Event ID: ${this.state.eventId}`);
        const body = encodeURIComponent(`
Error Details:
- Event ID: ${this.state.eventId}
- Error: ${this.state.error?.message}
- Page: ${window.location.href}
- Time: ${new Date().toISOString()}
- User Agent: ${navigator.userAgent}

Please describe what you were doing when this error occurred:
[Your description here]
    `);

        window.open(`mailto:support@trackTrace.com?subject=${subject}&body=${body}`);
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI based on error type or component
            if (this.props.fallback) {
                return this.props.fallback(this.state.error, this.handleRetry);
            }

            // Different UI for different environments
            if (process.env.NODE_ENV === 'development') {
                return this.renderDevelopmentError();
            }

            return this.renderProductionError();
        }

        return this.props.children;
    }

    renderDevelopmentError() {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-4xl w-full bg-white rounded-xl shadow-lg overflow-hidden">
                    {/* Header */}
                    <div className="bg-red-500 text-white p-6">
                        <div className="flex items-center">
                            <Bug className="w-8 h-8 mr-3" />
                            <div>
                                <h1 className="text-2xl font-bold">Development Error</h1>
                                <p className="text-red-100 mt-1">
                                    An error occurred during development - check the details below
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Error Details */}
                    <div className="p-6 space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Message</h3>
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <code className="text-red-800 text-sm font-mono">
                                    {this.state.error?.message}
                                </code>
                            </div>
                        </div>

                        {this.state.error?.stack && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Stack Trace</h3>
                                <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 max-h-64 overflow-auto">
                                    <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap">
                                        {this.state.error.stack}
                                    </pre>
                                </div>
                            </div>
                        )}

                        {this.state.errorInfo?.componentStack && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Component Stack</h3>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-h-64 overflow-auto">
                                    <pre className="text-xs font-mono text-blue-800 whitespace-pre-wrap">
                                        {this.state.errorInfo.componentStack}
                                    </pre>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                            <div className="text-sm text-gray-500">
                                Event ID: <code className="bg-gray-100 px-2 py-1 rounded">{this.state.eventId}</code>
                            </div>
                            <div className="flex space-x-3">
                                <Button variant="outline" onClick={this.handleRetry}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Try Again
                                </Button>
                                <Button variant="outline" onClick={this.handleReload}>
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Reload Page
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderProductionError() {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden text-center">
                    {/* Error Icon */}
                    <div className="bg-red-500 text-white p-8">
                        <AlertTriangle className="w-16 h-16 mx-auto mb-4" />
                        <h1 className="text-2xl font-bold">Oops! Something went wrong</h1>
                    </div>

                    {/* Error Message */}
                    <div className="p-8">
                        <p className="text-gray-600 mb-6">
                            We're sorry, but something unexpected happened. Our team has been notified
                            and is working to fix the issue.
                        </p>

                        {/* Error ID */}
                        <div className="bg-gray-100 rounded-lg p-4 mb-6">
                            <p className="text-sm text-gray-500 mb-1">Error ID for support:</p>
                            <code className="text-sm font-mono text-gray-800">{this.state.eventId}</code>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3">
                            <Button
                                variant="primary"
                                fullWidth
                                onClick={this.handleRetry}
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Try Again
                            </Button>

                            <Button
                                variant="outline"
                                fullWidth
                                onClick={this.handleReload}
                            >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reload Page
                            </Button>

                            <Button
                                variant="outline"
                                fullWidth
                                onClick={this.handleGoHome}
                            >
                                <Home className="w-4 h-4 mr-2" />
                                Go to Dashboard
                            </Button>
                        </div>

                        {/* Support Link */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <p className="text-sm text-gray-500 mb-3">
                                Still having trouble?
                            </p>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={this.reportIssue}
                            >
                                <Mail className="w-4 h-4 mr-2" />
                                Report Issue
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

// Higher-order component for easier usage
export const withErrorBoundary = (Component, fallback) => {
    return (props) => (
        <ErrorBoundary fallback={fallback}>
            <Component {...props} />
        </ErrorBoundary>
    );
};

// Hook for manual error reporting
export const useErrorHandler = () => {
    const reportError = (error, context = {}) => {
        // Log error manually
        console.error('Manual error report:', error, context);

        // In production, send to logging service
        if (process.env.NODE_ENV === 'production') {
            // Send to your logging service
            const errorData = {
                message: error.message,
                stack: error.stack,
                context,
                timestamp: new Date().toISOString(),
                url: window.location.href
            };

            // Replace with your actual logging service call
            console.log('Error would be sent to logging service:', errorData);
        }
    };

    return { reportError };
};

// Specific error boundaries for different sections
export const DashboardErrorBoundary = ({ children }) => (
    <ErrorBoundary fallback={(error, retry) => (
        <div className="p-6 text-center bg-white rounded-lg border border-red-200">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Dashboard Error</h3>
            <p className="text-gray-600 mb-4">Unable to load dashboard data.</p>
            <Button variant="primary" onClick={retry}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
            </Button>
        </div>
    )}>
        {children}
    </ErrorBoundary>
);

export const JobsErrorBoundary = ({ children }) => (
    <ErrorBoundary fallback={(error, retry) => (
        <div className="p-6 text-center bg-white rounded-lg border border-red-200">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Jobs Loading Error</h3>
            <p className="text-gray-600 mb-4">Unable to load jobs data.</p>
            <div className="flex justify-center space-x-3">
                <Button variant="outline" onClick={retry}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry
                </Button>
                <Button variant="primary" onClick={() => window.location.href = '/dashboard'}>
                    <Home className="w-4 h-4 mr-2" />
                    Go to Dashboard
                </Button>
            </div>
        </div>
    )}>
        {children}
    </ErrorBoundary>
);

export const TrackingErrorBoundary = ({ children }) => (
    <ErrorBoundary fallback={(error, retry) => (
        <div className="p-6 text-center bg-white rounded-lg border border-red-200">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Tracking Error</h3>
            <p className="text-gray-600 mb-4">Unable to load tracking information.</p>
            <Button variant="primary" onClick={retry}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
            </Button>
        </div>
    )}>
        {children}
    </ErrorBoundary>
);

export default ErrorBoundary;